/**
 * Standard spacing constants used throughout the application
 */

// Base spacing unit in pixels
export const SPACING_UNIT = 8;

// Layout spacing - used for gaps between major components
export const LAYOUT_SPACING = {
  xs: SPACING_UNIT, // 8px
  sm: SPACING_UNIT * 1.5, // 12px
  md: SPACING_UNIT * 2, // 16px
  lg: SPACING_UNIT * 3, // 24px
  xl: SPACING_UNIT * 4, // 32px
} as const;

// Component spacing - the standard gap between floating components (navbar, header, content)
export const COMPONENT_GAP = SPACING_UNIT; // 8px - matches the left-2 (8px) on navbar

// Navbar dimensions
export const NAVBAR_WIDTH = 80; // w-20 in tailwind
export const NAVBAR_OFFSET = NAVBAR_WIDTH + COMPONENT_GAP; // 88px total

// Header dimensions
export const HEADER_HEIGHT = 64; // h-16 in tailwind
export const HEADER_TOP_POSITION = 0; // top-0 in tailwind (no gap)
export const HEADER_OFFSET = HEADER_HEIGHT; // 64px total

// Working area offsets - includes gap on all sides
export const WORKING_AREA_LEFT_OFFSET = NAVBAR_OFFSET + COMPONENT_GAP; // 96px (88px navbar + 8px gap)
export const WORKING_AREA_TOP_OFFSET = HEADER_HEIGHT; // 64px (just the header height - DEPRECATED, use CONTENT_TOP_OFFSET)

// Content area positioning - accounts for header position and gap
export const HEADER_TOP_OFFSET = COMPONENT_GAP * 2; // 16px - where header is positioned
export const CONTENT_TOP_OFFSET = HEADER_TOP_OFFSET + HEADER_HEIGHT + COMPONENT_GAP; // 88px (16 + 64 + 8) - where content should start

// Corporate header uses top-3 (12px) + h-16 (64px) in CorporateHeader.tsx
export const CORPORATE_HEADER_TOP_OFFSET = LAYOUT_SPACING.sm + HEADER_HEIGHT; // 76px
export const CORPORATE_CONTENT_TOP_OFFSET = CORPORATE_HEADER_TOP_OFFSET + COMPONENT_GAP; // 84px

// BackOffice sidebar dimensions
export const BACKOFFICE_SIDEBAR_WIDTH = 288; // w-72 in tailwind (expanded state)
export const BACKOFFICE_SIDEBAR_WIDTH_COLLAPSED = 64; // w-16 in tailwind (collapsed state)
export const BACKOFFICE_SIDEBAR_LEFT_OFFSET = NAVBAR_OFFSET + COMPONENT_GAP * 2; // 96px (navbar + double gap)

// Modal and sheet positioning - use this for consistent positioning below the header
export const MODAL_TOP_OFFSET = HEADER_HEIGHT; // Same as WORKING_AREA_TOP_OFFSET
export const SHEET_TOP_OFFSET = HEADER_HEIGHT; // Same as WORKING_AREA_TOP_OFFSET

// Tailwind class helpers
export const SPACING_CLASSES = {
  componentGap: 'gap-2', // 8px
  navbarWidth: 'w-20', // 80px
  headerHeight: 'h-16', // 64px
  floatingRadius: 'rounded-3xl',
  floatingShadow: 'shadow-xl',
} as const;

// Floating Panel/Card Design System
// Clean white cards with dark PMX shadows
export const FLOATING_PANEL_CLASSES = {
  // Base panel - clean white with neutral border and dark shadow
  base: 'bg-white overflow-hidden rounded-3xl shadow-teal-xl border border-gray-200',
  // Common variants
  withBorder: 'bg-white overflow-hidden rounded-3xl shadow-teal-xl border border-gray-200',
  elevated: 'bg-white overflow-hidden rounded-3xl shadow-teal-xl',
  subtle: 'bg-white overflow-hidden rounded-3xl shadow-teal-lg border border-gray-100',
  // For member app (same styling now)
  neutral: 'bg-white overflow-hidden rounded-3xl shadow-xl',
  neutralWithBorder: 'bg-white overflow-hidden rounded-3xl shadow-xl border border-gray-100',
  // For dark mode support in future
  dark: 'bg-gray-900 overflow-hidden rounded-3xl shadow-xl',
} as const;

// Floating panel helper - use this for consistent floating card appearance
export const getFloatingPanelClass = (variant: keyof typeof FLOATING_PANEL_CLASSES = 'base') => {
  return FLOATING_PANEL_CLASSES[variant];
};

// Entity Card Design System
// Consistent card styling for user/item cards across all pages
export const ENTITY_CARD_CLASSES = {
  base: 'bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors duration-150',
  selected: 'bg-primary text-white border-primary',
  interactive:
    'bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:shadow-sm transition-[colors,border-color,box-shadow] duration-150 cursor-pointer',
} as const;

export const getEntityCardClass = (variant: keyof typeof ENTITY_CARD_CLASSES = 'base') => {
  return ENTITY_CARD_CLASSES[variant];
};

// CSS variables for dynamic use
export const SPACING_CSS_VARS = {
  '--component-gap': `${COMPONENT_GAP}px`,
  '--navbar-width': `${NAVBAR_WIDTH}px`,
  '--navbar-offset': `${NAVBAR_OFFSET}px`,
  '--header-height': `${HEADER_HEIGHT}px`,
  '--header-offset': `${HEADER_OFFSET}px`,
} as const;

// Background opacity for main app background (percentage of primary color)
export const APP_BACKGROUND_OPACITY = 0.05; // 5% opacity, can be adjusted to 0.1 (10%) or 0.3 (30%)

// Development helper - shows visual boundary of the working area
export const SHOW_WORKING_AREA_BOUNDARY = true; // Set to true to see the working area outline
export const WORKING_AREA_BOUNDARY_COLOR = 'rgba(255, 0, 0, 0.5)'; // Red with 50% opacity
export const WORKING_AREA_BOUNDARY_WIDTH = '3px'; // Thick border

/**
 * Layout Height Calculations
 *
 * These utilities provide consistent viewport height calculations for panels.
 * Use these instead of hardcoding calc() values throughout the app.
 */

// Panel height configurations for different layout types
export const PANEL_HEIGHT_CONFIG = {
  // Full page layouts (Hub, single-panel pages)
  FULL_PAGE: {
    // Accounts for: p-2 wrapper padding (8px top + 8px bottom) + additional margin buffer
    topMargin: COMPONENT_GAP * 2, // 16px (wrapper padding)
    bottomMargin: COMPONENT_GAP * 2, // 16px (wrapper padding)
  },

  // Sidebar layouts (Studio, UserManagement)
  WITH_SIDEBAR: {
    // Accounts for: top margin + bottom margin + gap buffer
    topMargin: COMPONENT_GAP, // 8px
    bottomMargin: COMPONENT_GAP * 2, // 16px
    sideMargin: COMPONENT_GAP, // 8px (left/right margins)
  },

  // Content panels inside layouts
  CONTENT_PANEL: {
    // Minimal margins for panels inside other containers
    topMargin: COMPONENT_GAP, // 8px
    bottomMargin: COMPONENT_GAP, // 8px
  },
} as const;

/**
 * Calculate viewport-based height for full-page layouts
 * Used in: Hub, ThriveDashboard (main grid/container)
 *
 * @param config - Optional custom margin configuration
 * @returns CSS calc() string for height
 */
export const getFullPageHeight = (config = PANEL_HEIGHT_CONFIG.FULL_PAGE) => {
  const totalMargin = config.topMargin + config.bottomMargin;
  return `calc(var(--app-content-height, 100dvh) - ${totalMargin}px)`;
};

/**
 * Calculate viewport-based height for sidebar layouts
 * Used in: Studio sidebar, UserManagement sidebar
 *
 * @param config - Optional custom margin configuration
 * @returns CSS calc() string for height
 */
export const getSidebarLayoutHeight = (config = PANEL_HEIGHT_CONFIG.WITH_SIDEBAR) => {
  const totalVerticalMargin = config.topMargin + config.bottomMargin + COMPONENT_GAP; // Extra gap for spacing
  return `calc(var(--app-content-height, 100dvh) - ${totalVerticalMargin}px)`;
};

/**
 * Calculate viewport-based height for content panels
 * Used in: Individual panels that need to fill remaining space
 *
 * @param additionalOffset - Additional pixels to subtract (e.g., header height)
 * @returns CSS calc() string for height
 */
export const getContentPanelHeight = (additionalOffset = 0) => {
  const config = PANEL_HEIGHT_CONFIG.CONTENT_PANEL;
  const totalMargin = config.topMargin + config.bottomMargin + additionalOffset;
  return `calc(var(--app-content-height, 100dvh) - ${totalMargin}px)`;
};

/**
 * Common height presets for different layout patterns
 * Use these for consistency across the application
 */
export const LAYOUT_HEIGHTS = {
  // Hub 3-column grid height
  HUB_GRID: getFullPageHeight(),

  // Sidebar panels (Studio, UserManagement)
  SIDEBAR_PANEL: getSidebarLayoutHeight(),

  // Single full-page panel
  FULL_PAGE_PANEL: getFullPageHeight({
    topMargin: COMPONENT_GAP * 2,
    bottomMargin: COMPONENT_GAP * 2,
  }),

  // Content area in sidebar layouts
  SIDEBAR_CONTENT: getSidebarLayoutHeight(),
} as const;

/**
 * Helper function to create custom height calculations
 * For special cases that don't fit the standard patterns
 *
 * @param topOffset - Pixels to subtract from top
 * @param bottomOffset - Pixels to subtract from bottom
 * @returns CSS calc() string for height
 */
export const createCustomHeight = (topOffset: number, bottomOffset: number) => {
  const totalOffset = topOffset + bottomOffset;
  return `calc(var(--app-content-height, 100dvh) - ${totalOffset}px)`;
};
